/**
 * RBAC Service - Role-Based Access Control
 * 
 * Implements permission checking logic for the Admin Ops UI.
 * Enforces role-based permissions and business rules like
 * preventing self-approval in the review workflow.
 */

import logger from '../core/logger.js';
import { 
  Role, 
  Permission, 
  ROLE_PERMISSIONS,
  User,
  PermissionCheckResult,
  PermissionContext,
  ClipState,
  ChangeType
} from '../types/rbac.js';

/**
 * RBACService class - Manages role-based access control
 */
class RBACService {
  /**
   * Check if a user has a specific permission
   * 
   * @param user - User to check permissions for
   * @param permission - Permission to check
   * @returns true if user has permission, false otherwise
   */
  hasPermission(user: User | null, permission: Permission): boolean {
    if (!user || !user.role) {
      logger.debug('rbac: No user or no role, permission denied', { permission, hasUser: !!user });
      return false;
    }

    const rolePermissions = ROLE_PERMISSIONS[user.role];
    if (!rolePermissions) {
      logger.warn('rbac: Invalid role', { role: user.role });
      return false;
    }
    
    const hasPermission = rolePermissions.includes(permission);
    
    logger.debug('rbac: Permission check', { 
      userId: user.id, 
      role: user.role, 
      permission, 
      granted: hasPermission 
    });
    
    return hasPermission;
  }

  /**
   * Check if a user has any of the specified permissions
   * 
   * @param user - User to check permissions for
   * @param permissions - Array of permissions to check
   * @returns true if user has at least one permission
   */
  hasAnyPermission(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    
    return permissions.some(permission => this.hasPermission(user, permission));
  }

  /**
   * Check if a user has all of the specified permissions
   * 
   * @param user - User to check permissions for
   * @param permissions - Array of permissions to check
   * @returns true if user has all permissions
   */
  hasAllPermissions(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    
    return permissions.every(permission => this.hasPermission(user, permission));
  }

  /**
   * Get all permissions for a user's role
   * 
   * @param user - User to get permissions for
   * @returns Array of permissions (empty for users with no role)
   */
  getUserPermissions(user: User | null): Permission[] {
    if (!user || !user.role) return [];
    
    return ROLE_PERMISSIONS[user.role] || [];
  }

  /**
   * Check if a user can approve content (with self-approval prevention)
   * 
   * This implements the business rule: "must be a Reviewer or Admin
   * (not the same person who drafted the clip when possible)"
   * 
   * @param user - User attempting to approve
   * @param permission - Specific approval permission (APPROVE_TEXT or APPROVE_AUDIO)
   * @param context - Context including who created the content
   * @returns Permission check result with reason if denied
   */
  canApprove(
    user: User | null, 
    permission: Permission.APPROVE_TEXT | Permission.APPROVE_AUDIO,
    context: PermissionContext
  ): PermissionCheckResult {
    // Runtime validation: ensure this is an approval permission
    if (permission !== Permission.APPROVE_TEXT && permission !== Permission.APPROVE_AUDIO) {
      logger.error('rbac: canApprove called with non-approval permission', { permission });
      return {
        allowed: false,
        reason: 'Invalid permission type for approval check'
      };
    }

    // First check base permission
    if (!this.hasPermission(user, permission)) {
      return {
        allowed: false,
        reason: 'User does not have approval permission'
      };
    }

    // Check self-approval rule
    if (context.createdBy && user && context.createdBy === user.id) {
      return {
        allowed: false,
        reason: 'Cannot approve own content'
      };
    }

    return { allowed: true };
  }

  /**
   * Check if a user can perform a state transition
   * 
   * Validates both permission and current state appropriateness.
   * 
   * @param user - User attempting transition
   * @param fromState - Current clip state
   * @param toState - Target clip state
   * @param context - Additional context
   * @returns Permission check result
   */
  canTransitionState(
    user: User | null,
    fromState: ClipState,
    toState: ClipState,
    context: PermissionContext = {}
  ): PermissionCheckResult {
    if (!user) {
      return { allowed: false, reason: 'No user authenticated' };
    }

    // Define state transition permission requirements
    const transitionRules: Record<string, Permission> = {
      [`${ClipState.Draft}->${ClipState.TextReview}`]: Permission.SUBMIT_FOR_REVIEW,
      [`${ClipState.TextReview}->${ClipState.TextApproved}`]: Permission.APPROVE_TEXT,
      [`${ClipState.TextApproved}->${ClipState.AudioQueued}`]: Permission.TRIGGER_AUDIO_GENERATION,
      [`${ClipState.AudioReady}->${ClipState.AudioReview}`]: Permission.APPROVE_AUDIO, // Auto or manual trigger
      [`${ClipState.AudioReview}->${ClipState.AudioApproved}`]: Permission.APPROVE_AUDIO,
      [`${ClipState.AudioApproved}->${ClipState.Published}`]: Permission.PUBLISH_CLIP,
    };

    const transitionKey = `${fromState}->${toState}`;
    const requiredPermission = transitionRules[transitionKey];

    if (!requiredPermission) {
      return { 
        allowed: false, 
        reason: `Invalid state transition: ${fromState} -> ${toState}` 
      };
    }

    // Check if transition requires approval permission (and enforce self-approval rule)
    if (requiredPermission === Permission.APPROVE_TEXT || 
        requiredPermission === Permission.APPROVE_AUDIO) {
      return this.canApprove(user, requiredPermission, context);
    }

    // Check base permission
    if (!this.hasPermission(user, requiredPermission)) {
      return {
        allowed: false,
        reason: `Missing required permission: ${requiredPermission}`
      };
    }

    return { allowed: true };
  }

  /**
   * Check if a user can publish content
   * 
   * Only Admins can publish, with optional toggle for Reviewers on MinorFix changes.
   * 
   * @param user - User attempting to publish
   * @param context - Context including change type
   * @param minorFixAutoPublishEnabled - Optional toggle for MinorFix auto-publish
   * @returns Permission check result
   */
  canPublish(
    user: User | null,
    context: PermissionContext,
    minorFixAutoPublishEnabled: boolean = false
  ): PermissionCheckResult {
    if (!user) {
      return { allowed: false, reason: 'No user authenticated' };
    }

    // Admins can always publish
    if (this.hasPermission(user, Permission.PUBLISH_CLIP)) {
      return { allowed: true };
    }

    // Check MinorFix auto-publish toggle
    if (minorFixAutoPublishEnabled && 
        context.changeType === ChangeType.MinorFix &&
        user.role === Role.Reviewer) {
      return { allowed: true };
    }

    return {
      allowed: false,
      reason: 'Only Admins can publish (or Reviewers for MinorFix when enabled)'
    };
  }

  /**
   * Check if user is an Admin
   */
  isAdmin(user: User | null): boolean {
    return user?.role === Role.Admin;
  }

  /**
   * Check if user is an Editor
   */
  isEditor(user: User | null): boolean {
    return user?.role === Role.Editor;
  }

  /**
   * Check if user is a Reviewer
   */
  isReviewer(user: User | null): boolean {
    return user?.role === Role.Reviewer;
  }

  /**
   * Get role display name
   */
  getRoleDisplayName(role: Role): string {
    const displayNames: Record<Role, string> = {
      [Role.Admin]: 'Administrator',
      [Role.Editor]: 'Editor',
      [Role.Reviewer]: 'Reviewer'
    };
    
    return displayNames[role] || role;
  }

  /**
   * Get all available roles
   */
  getAllRoles(): Role[] {
    return Object.values(Role);
  }

  /**
   * Validate if a role value is valid
   */
  isValidRole(role: string): role is Role {
    return Object.values(Role).includes(role as Role);
  }
}

// Create and export singleton instance
export const rbacService = new RBACService();

// Export class for testing
export { RBACService };
